// src/pages/PatientTodayView.jsx
//
// The Today screen's content — a "Health Score" hero chart, an attention
// banner, a horizontally-scrolling "Your Chapters" row, and quick actions.
// Shared by both portal themes (PatientSettingsPage's "Appearance" section):
// only the colors differ, via the var(--token) values .app-shell's
// data-theme attribute switches between (see index.css's
// .app-shell[data-theme="dark"] block) — this file has no theme branching
// of its own. Rendered by PatientTodayPage, which does the data fetching
// (summary/journey); this component is pure presentation over
// that same data.
//
// "Health Score" is a new framing over the existing backend field, not a
// new one: HealthJourneyDTO.Milestone.score is a RISK score (lower is
// healthier — see HealthJourneyService), while this design shows a score
// that climbs as the patient improves. healthScore = 100 - riskScore makes
// that flip without needing a backend change; every riskScore observed so
// far (22-78) already lives on a 0-100 scale, so the inversion is exact.

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../components/Icon';
import VisitReadyCard from '../components/VisitReadyCard';
import { formatShortDate, AskPreviuBar } from './PatientTodayPage';
import { formatMarkerText } from '../utils/markerNames';
import {
  buildScoreNodes, computeTrendPct, insertStartedCare, interpolateYForDate, smoothPathCommands,
} from '../utils/healthScoreChart';
import { isLabTestSuggestion, LAB_TEST_ORDER_PATH } from '../utils/labTestSuggestion';

// viewBox units chosen to read like real pixels rather than a normalized
// 0-100 box. CHART_W is measured from the rendered wrapper (see
// HealthScoreChart) rather than fixed, so the viewBox-to-CSS-pixel scale
// stays ~1:1 whether the visible plot area is a phone-width card or a
// full-width desktop one. DEFAULT_CHART_W is only the pre-measurement
// fallback for the very first paint.
const DEFAULT_CHART_W = 320;
// CHART_H isn't a constant here — HealthScoreChart picks it from the
// container's width (flatter on a phone-width card) — see its own comment.
const PAD_X = 12; // breathing room so the first/last dot isn't clipped
// A label's own content height: a 10px date line + a description that wraps
// to at most two 11px lines (1.3 line-height). The description used to be a
// single ellipsised line, which cut "HDL cholesterol +5 markers monitored"
// down to "HDL cholesterol +5 ma…" on a phone. An earlier pass measured this
// wrong (20px) and its label row overlapped the row next to it; every offset
// below is sized against this same number.
const LABEL_H = 38;
// Gap between the plot's own top/bottom edge and the label row nearest it.
// Also what clears an "above" label from a non-latest point's own
// score-number annotation just above its dot (see PAD_TOP below) — the
// score number needs ~8.5px of that gap for itself, so this has to be
// bigger than 8.5, not just any small breathing-room value.
const LABEL_GAP = 14;
// PAD_TOP: headroom for a non-latest point's own score-number annotation
// (drawn just above its dot) PLUS — for an odd-indexed point — that same
// point's date/label alternating to ABOVE the plot instead of its usual
// spot below it (see HealthScoreChart's per-point render: labelAbove).
// Sized so the two never overlap even when the topmost-scored point on the
// chart happens to land on an odd index.
const PAD_TOP = 59;
// PAD_BOTTOM: room for one label row — every point alternates between this
// row and the mirrored one above (PAD_TOP), so unlike an earlier version of
// this chart that stacked odd-indexed labels into a second row here, this
// only ever needs to fit one.
const PAD_BOTTOM = LABEL_GAP + LABEL_H + 9;
// Every node on this chart — baseline, "Started care", latest, and the
// future/tentative point, whichever of those exist — sits at an equal,
// fixed gap from its neighbors (see HealthScoreChart's `spacing`),
// VISIBLE_COUNT of them spanning the plot edge-to-edge with no scrolling
// needed; any further nodes continue at that same spacing past the right
// edge and are reached by scrolling. LABEL_W (each point's date/
// description box) is wide enough for the longest label this chart
// actually shows — "Follow-up planned →".
const VISIBLE_COUNT_MOBILE = 3;
const VISIBLE_COUNT_DESKTOP = 4;
const LABEL_W = 130;

// Where the tentative follow-up is booked — with the patient's Navigator.
const NAVIGATOR_BOOKING_PATH = '/patient/appointments/navigator';

// The graph plots the health score (100 − risk score) by default. Staff can switch it to the risk score, which is the same line read the other
// way round: every scored node's value becomes 100 − health.
const asMetric = (nodes, metric) => (metric === 'risk'
  ? nodes.map(n => (n.healthScore == null ? n : { ...n, healthScore: 100 - n.healthScore }))
  : nodes);

function HealthScoreChart({ journey, metric = 'health' }) {
  // Measured synchronously (getBoundingClientRect, inside a layout effect) so
  // the very first paint already uses the real container width instead of
  // flashing DEFAULT_CHART_W first; the ResizeObserver then keeps it in sync
  // across breakpoint changes (mobile card ↔ full-width desktop hero). This
  // measures the SCROLLABLE plot area specifically (the ref sits on that div,
  // not the axis column beside it or the chart as a whole) — its own
  // allocated box width doesn't change when its (possibly wider) scrolling
  // content does, so it stays a true read of the visible viewport.
  const containerRef = useRef(null);
  const [measuredWidth, setMeasuredWidth] = useState(DEFAULT_CHART_W);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setMeasuredWidth(el.getBoundingClientRect().width || DEFAULT_CHART_W);
    const observer = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect?.width;
      if (width) setMeasuredWidth(width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // past[0] is baseline, tagged explicitly rather than inferred from
  // position — once "Started care" is folded into the sequence below it
  // can itself land at index 0 (registration can predate a brought-in
  // historical baseline report), and the "Baseline" label must still
  // follow the actual baseline milestone, not whichever node ends up
  // first.
  const past = asMetric(buildScoreNodes(journey.milestones, null), metric)
    .map((p, i) => ({ ...p, isBaseline: i === 0 }));
  const withFuture = asMetric(buildScoreNodes(journey.milestones, journey.nextMilestone), metric);
  const future = withFuture.length > past.length ? withFuture[withFuture.length - 1] : null;

  // baseline + "Started care" + latest, in date order (insertStartedCare
  // no-ops if there's no registration date), then the future/tentative
  // point always last — it's a projection, not a dated fact to sort by.
  const timelineBase = insertStartedCare(past, journey.startedAt);
  const timeline = future ? [...timelineBase, future] : timelineBase;
  const n = timeline.length;

  const updateScrollButtons = React.useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  }, []);

  // Defaults to the LEFT (baseline/"Started care"/latest, no scrolling
  // needed) rather than jumping straight to the upcoming/tentative point —
  // that point is deliberately placed at the same fixed spacing past
  // whichever nodes fit in view, off the right edge, and scrolling RIGHT
  // is how you reveal it.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollLeft = 0;
    updateScrollButtons();
  }, [n, measuredWidth, updateScrollButtons]);

  if (past.length === 0) {
    return (
      <div ref={containerRef} style={{ padding: '32px 4px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
        Your health score will show up here once you have a report.
      </div>
    );
  }

  const containerWidth = Math.round(measuredWidth) || DEFAULT_CHART_W;
  // Flatter on a phone-width card so the hero fits more of the screen above
  // the fold alongside the chapter cards and the "Ask about your health
  // story" bar below it — the fixed 170 was tuned back when the chart sat
  // in a narrower, unmeasured mobile card and had no such competition for
  // vertical space.
  const CHART_H = containerWidth < 400 ? 190 : 217;

  const latestScore = past[past.length - 1].healthScore;
  const scores = past.map(p => p.healthScore);
  const minScore = Math.min(...scores);
  const maxScore = Math.max(...scores);
  // A little headroom above/below the real min/max so the line never
  // touches the plot's top or bottom edge.
  const axisMin = Math.max(0, Math.floor((minScore - 8) / 10) * 10);
  const axisMax = Math.min(100, Math.ceil((maxScore + 8) / 10) * 10);
  const axisRange = Math.max(10, axisMax - axisMin);

  const plotH = CHART_H - PAD_TOP - PAD_BOTTOM;

  // Equal spacing, viewport-windowed: VISIBLE_COUNT nodes span the plot
  // edge-to-edge (the first at the left edge, the VISIBLE_COUNT-th at the
  // right edge) — 3 on a phone-width card, 4 on anything wider. Any
  // further nodes sit past the right edge at that same fixed spacing and
  // are reached by scrolling — same scroll mechanism as before, just
  // driven by "how many nodes actually exist" rather than a hardcoded
  // single future-point offset. Replaces the old "baseline left edge /
  // latest centered / future off past pastPlotW's edge" special-casing,
  // which only ever handled exactly 2-3 nodes.
  const visibleCount = containerWidth < 480 ? VISIBLE_COUNT_MOBILE : VISIBLE_COUNT_DESKTOP;
  // When more nodes continue past the right edge, the last visible node's
  // label (LABEL_W wide, centred on it) must still fit inside the viewport —
  // otherwise "21 Aug 2…" / "LDL +3 marke…" is cut off by the scroller. Inset
  // the right edge by half a label in that case.
  const rightInset = n > visibleCount ? Math.max(PAD_X, LABEL_W / 2) : PAD_X;
  const pastPlotW = containerWidth - PAD_X - rightInset;
  const spacing = pastPlotW / (visibleCount - 1);
  const CHART_W = PAD_X * 2 + Math.max(0, n - 1) * spacing;
  const scrollable = CHART_W > containerWidth + 1;

  // Pass 1: x for every node (sequential, equally spaced) and y for every
  // node with a real score (past) or that levels with the latest one
  // (future). "Started care" has neither yet — its y depends on every
  // past point's y already being known, so it's solved in pass 2 below.
  const scoredForInterpolation = [];
  const withXY = timeline.map((node, i) => {
    const x = PAD_X + i * spacing;
    let y = null;
    if (node.kind === 'past') {
      y = PAD_TOP + plotH * (1 - (node.healthScore - axisMin) / axisRange);
      scoredForInterpolation.push({ date: node.date, y });
    } else if (node.kind === 'future') {
      y = PAD_TOP + plotH * (1 - (latestScore - axisMin) / axisRange); // levels with the latest real reading
    }
    return { ...node, x, y };
  });

  // Pass 2: "Started care" sits on the interpolated score line between its
  // neighboring scored points (or clamps to whichever end it falls
  // outside of) — same idea the old x+y overlay used, just for y alone
  // now that x is a fixed sequential slot.
  const points = withXY.map(p =>
    p.kind === 'started' ? { ...p, y: interpolateYForDate(scoredForInterpolation, p.date) } : p);

  const latestIdx = points.map(p => p.kind).lastIndexOf('past');

  const solidPoints = points.filter(p => p.kind !== 'future');
  const solidPathD = solidPoints.length > 1
    ? `M ${solidPoints[0].x},${solidPoints[0].y}` + smoothPathCommands(solidPoints)
    : null;
  const areaPathD = solidPoints.length > 1
    ? `M ${PAD_X},${CHART_H - PAD_BOTTOM} L ${solidPoints[0].x},${solidPoints[0].y}`
      + smoothPathCommands(solidPoints)
      + ` L ${solidPoints[solidPoints.length - 1].x},${CHART_H - PAD_BOTTOM} Z`
    : null;
  const dashedPath = future
    ? `${solidPoints[solidPoints.length - 1].x},${solidPoints[solidPoints.length - 1].y} ${points[n - 1].x},${points[n - 1].y}`
    : null;

  function scrollByPage(direction) {
    const el = containerRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * containerWidth * 0.8, behavior: 'smooth' });
  }

  const legendItem = { display: 'inline-flex', alignItems: 'center', gap: 6 };
  return (
    <div>
    <div style={{ position: 'relative', display: 'flex' }}>
      <div
        ref={containerRef}
        onScroll={updateScrollButtons}
        className="today-chart-scroll"
        style={{
          flex: 1, minWidth: 0, overflowX: 'auto', scrollBehavior: 'smooth',
          // Fade the right edge while more nodes continue past it, so the
          // next point's label reads as "scroll for more" instead of as text
          // cut off mid-word ("Fo…").
          ...(canScrollRight ? {
            WebkitMaskImage: 'linear-gradient(to right, black calc(100% - 36px), transparent)',
            maskImage: 'linear-gradient(to right, black calc(100% - 36px), transparent)',
          } : null),
        }}
      >
        <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} width={CHART_W} height={CHART_H}
          role="img" aria-label="Health score trend">
          <defs>
            <linearGradient id="healthScoreFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--teal)" stopOpacity="0.5" />
              <stop offset="45%" stopColor="var(--teal)" stopOpacity="0.18" />
              <stop offset="100%" stopColor="var(--teal)" stopOpacity="0" />
            </linearGradient>
            <radialGradient id="healthScoreGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="var(--teal)" stopOpacity="var(--chart-glow-inner)" />
              <stop offset="60%" stopColor="var(--teal)" stopOpacity="var(--chart-glow-mid)" />
              <stop offset="100%" stopColor="var(--teal)" stopOpacity="0" />
            </radialGradient>
          </defs>
          {areaPathD && <path d={areaPathD} fill="url(#healthScoreFill)" />}
          {solidPathD && (
            <path d={solidPathD} fill="none" stroke="var(--teal)" strokeWidth="2.5"
              strokeLinecap="round" strokeLinejoin="round" />
          )}
          {dashedPath && (
            <polyline points={dashedPath} fill="none" stroke="var(--amber)" strokeWidth="2"
              strokeDasharray="5 5" strokeLinecap="round" />
          )}

          {points.map((p, i) => {
            const isLatest = i === latestIdx;
            const isFuture = p.kind === 'future';
            // "Started care" gets the same solid-dot treatment as a real
            // reviewed report — product feedback was that it should read
            // as one more node on this timeline, not a lesser marker —
            // and now runs through this same shared per-point rendering
            // (date + label alternating above/below, same as every other
            // point) instead of a separate floating overlay with no date
            // of its own.
            const isStarted = p.kind === 'started';
            // "Started care" is where Previu's intervention begins, so it gets
            // the intervention marker (blue dot + ring + halo) instead of a
            // teal node — see the legend below the chart.
            const solidDot = isLatest;
            const r = isLatest ? 7 : isFuture ? 6 : isStarted ? 5 : 4.5;
            // A centered label box would run off the canvas for a point
            // near either edge — the first point in particular, whose x sits
            // at PAD_X, far short of half LABEL_W. Clamping the BOX back
            // on-canvas alone left the TEXT centered around the clamped
            // box's own middle, nowhere near the point's real x (e.g. the
            // first point's label reading ~53px to the right of its own
            // node instead of under it). Left/right-aligning the text
            // specifically when the box was clamped keeps it hugging the
            // edge nearest the point instead of drifting into the box's
            // unclamped center — a point with room to breathe on both
            // sides still centers exactly as before.
            const naturalLabelX = p.x - LABEL_W / 2;
            const labelX = Math.max(0, Math.min(CHART_W - LABEL_W, naturalLabelX));
            const labelAlign = naturalLabelX < labelX ? 'left' : naturalLabelX > labelX ? 'right' : 'center';
            // Alternate each point's own date/label between the row below
            // the plot and a mirrored row above it — the first point's label
            // below its node, the second above, and so on — rather than
            // every label defaulting to the same side, which read as one
            // dense strip under the line instead of a label clearly paired
            // with its own point.
            const labelAbove = i % 2 === 1;
            const labelY = labelAbove
              ? PAD_TOP - LABEL_GAP - LABEL_H
              : CHART_H - PAD_BOTTOM + LABEL_GAP;
            return (
              <g key={`${p.date}-${p.kind}-${i}`}>
                {isLatest && <circle cx={p.x} cy={p.y} r={r * 3.4} fill="url(#healthScoreGlow)" />}
                {labelAbove && (
                  <line x1={p.x} y1={PAD_TOP - 2} x2={p.x} y2={labelY + LABEL_H + 2}
                    stroke="var(--border)" strokeWidth="1" />
                )}
                {!isLatest && (p.healthScore != null || isStarted) && (
                  <text x={p.x} y={p.y - r - 4} textAnchor="middle" fontSize="12" fontWeight="700"
                    fontFamily="var(--font-mono)" fill="var(--teal)">
                    {/* "Started care" has no reading of its own: it sits on the line, so it shows the value the line has there. */}
                    {p.healthScore ?? Math.round(axisMin + axisRange * (1 - (p.y - PAD_TOP) / plotH))}
                  </text>
                )}
                {isStarted && (
                  <>
                    <circle cx={p.x} cy={p.y} r={15} fill="var(--intervention-halo)" />
                    <circle cx={p.x} cy={p.y} r={11} fill="none" stroke="var(--intervention-ring)" strokeWidth="1.25" />
                  </>
                )}
                <circle
                  cx={p.x} cy={p.y} r={r}
                  fill={isStarted ? 'var(--intervention)' : solidDot ? 'var(--teal)' : 'var(--bg-surface)'}
                  stroke={isFuture ? 'var(--amber)' : solidDot ? 'none' : 'var(--teal)'}
                  strokeWidth={isFuture ? 1.5 : solidDot ? 0 : 2}
                  strokeDasharray={isFuture ? '2 2' : undefined}
                />
                {isStarted && <title>Previu intervention: care started {formatShortDate(p.date)}</title>}
                <foreignObject x={labelX} y={labelY} width={LABEL_W} height={LABEL_H}>
                  {/* eslint-disable-next-line react/no-unknown-property */}
                  <div xmlns="http://www.w3.org/1999/xhtml" style={{
                    textAlign: labelAlign,
                    fontFamily: 'var(--font-body)', lineHeight: 1.3, fontSize: 10,
                  }}>
                    <div style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      title={formatShortDate(p.date)}>{formatShortDate(p.date)}</div>
                    <div title={p.isBaseline ? 'Baseline' : p.label} style={{
                      color: isFuture ? 'var(--amber)' : isStarted ? 'var(--intervention)' : solidDot ? 'var(--teal)' : 'var(--text-muted)',
                      fontWeight: isLatest || isFuture ? 700 : 500, fontSize: 11,
                      overflow: 'hidden', overflowWrap: 'anywhere',
                      display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2,
                    }}>
                      {p.isBaseline ? 'Baseline' : isFuture ? `${p.label} →` : p.label}
                    </div>
                  </div>
                </foreignObject>
              </g>
            );
          })}
        </svg>
      </div>

      {scrollable && canScrollLeft && (
        <button type="button" aria-label="Scroll to earlier reports" onClick={() => scrollByPage(-1)}
          className="today-chart-nav today-chart-nav--left">
          <span aria-hidden="true" style={{ display: 'inline-flex', transform: 'scaleX(-1)' }}>
            <Icon name="chevronRight" size={14} />
          </span>
        </button>
      )}
      {scrollable && canScrollRight && (
        <button type="button" aria-label="Scroll to more recent reports" onClick={() => scrollByPage(1)}
          className="today-chart-nav today-chart-nav--right">
          <Icon name="chevronRight" size={14} />
        </button>
      )}
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 18px', marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
      <span style={legendItem}>
        <svg width="14" height="14" aria-hidden="true"><circle cx="7" cy="7" r="4.5" fill="var(--bg-surface)" stroke="var(--teal)" strokeWidth="2" /></svg>
        Lab report
      </span>
      {points.some(p => p.kind === 'started') && (
        <span style={legendItem}>
          <svg width="22" height="22" aria-hidden="true">
            <circle cx="11" cy="11" r="10" fill="var(--intervention-halo)" />
            <circle cx="11" cy="11" r="7.5" fill="none" stroke="var(--intervention-ring)" />
            <circle cx="11" cy="11" r="4" fill="var(--intervention)" />
          </svg>
          Previu intervention
        </span>
      )}
      {future && (
        <span style={legendItem}>
          <svg width="14" height="14" aria-hidden="true"><circle cx="7" cy="7" r="5" fill="var(--bg-surface)" stroke="var(--amber)" strokeDasharray="2 2" /></svg>
          Planned follow-up
        </span>
      )}
    </div>
    </div>
  );
}

function ChapterCard({ accent, accentBg, accentText, eyebrow, title, date, note, noteAction, onClick, children }) {
  const interactive = !!onClick;
  return (
    <div
      className="card today-chapter-card"
      style={{
        borderTop: `3px solid ${accent}`, display: 'flex', flexDirection: 'column', gap: 8,
        cursor: interactive ? 'pointer' : undefined,
      }}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={interactive ? (e => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
      }) : undefined}
    >
      <div className="today-chapter-eyebrow" style={{ color: accent }}>
        {eyebrow}
      </div>
      {date && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{formatShortDate(date)}</div>}
      {title && (
        <div className="today-chapter-title" style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.25 }}>
          {title}
        </div>
      )}
      {note && (
        <div className="today-chapter-note" style={{
          fontSize: 12, color: accentText || 'var(--text-secondary)', background: accentBg || 'var(--bg-subtle)',
          borderRadius: 'var(--radius-sm)', padding: '8px 10px', lineHeight: 1.4,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        }}>
          <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{note}</span>
          {noteAction && <span style={{ fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}>{noteAction} →</span>}
        </div>
      )}
      {children}
    </div>
  );
}

function QuickActionButton({ icon, label, sublabel, onClick }) {
  return (
    <button type="button" onClick={onClick} className="card today-quick-action" style={{
      margin: 0, flex: 1, display: 'flex', alignItems: 'center', gap: 10,
      cursor: 'pointer', textAlign: 'left', border: '1px solid var(--border)',
    }}>
      <div aria-hidden="true" className="today-quick-action-icon" style={{
        width: 34, height: 34, borderRadius: '50%', background: 'var(--bg-subtle)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'var(--teal)',
      }}>
        <Icon name={icon} size={16} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{label}</div>
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{sublabel}</div>
      </div>
    </button>
  );
}

const bookMenuItemStyle = {
  display: 'flex', alignItems: 'center', gap: 10, width: '100%',
  padding: '11px 14px', fontSize: 13, color: 'var(--text-primary)',
  background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
  fontFamily: 'var(--font-body)',
};

// Replaces the old lab-test-only "Book" quick action: the sublabel now
// covers both booking targets the patient portal actually has (a doctor
// visit and a lab test), so a single click can no longer pick one for them.
// Opens a small popover instead of a new page — there's no combined
// booking screen to send them to.
function BookQuickAction({ navigate }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    function handleKey(e) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const go = (to) => { setOpen(false); navigate(to); };

  return (
    <div ref={ref} style={{ position: 'relative', flex: 1 }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="card today-quick-action"
        aria-haspopup="true"
        aria-expanded={open}
        style={{
          margin: 0, width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          cursor: 'pointer', textAlign: 'left', border: '1px solid var(--border)',
        }}
      >
        <div aria-hidden="true" className="today-quick-action-icon" style={{
          width: 34, height: 34, borderRadius: 'var(--radius-md)', background: 'var(--teal-bg)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'var(--teal)',
        }}>
          <Icon name="calendar" size={16} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Book</div>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>Doctor · Lab test</div>
        </div>
      </button>

      {open && (
        <div role="menu" style={{
          position: 'absolute', bottom: 'calc(100% + 8px)', left: 0, right: 0,
          background: 'var(--bg-surface)', border: '1px solid var(--border)',
          borderRadius: 10, boxShadow: 'var(--shadow-md)', overflow: 'hidden', zIndex: 20,
        }}>
          <button role="menuitem" type="button" onClick={() => go('/patient/appointments/book')} style={bookMenuItemStyle}>
            <Icon name="calendar" size={14} /> Book a doctor visit
          </button>
          <div style={{ height: 1, background: 'var(--border)' }} />
          <button role="menuitem" type="button" onClick={() => go('/patient/lab-tests/order')} style={bookMenuItemStyle}>
            <Icon name="vial" size={14} /> Order a lab test
          </button>
        </div>
      )}
    </div>
  );
}

// First sentence of a care-plan description, minus the clarifying clause after
// its em dash — the same shortening PatientCarePlanPage uses for its cards.
function shortItemTitle(description) {
  const first = (description || '').split(/(?<=\.)\s+/)[0] || '';
  const dash = first.indexOf('\u2014');
  return (dash > -1 ? first.slice(0, dash) : first).replace(/\.\s*$/, '').trim() || 'Care plan item';
}

// Everything the patient has to do, as one row each, plus the headline count.
// Shared by the Metric-oriented screen (below) and the Simplified one
// (PatientTodaySimpleView), so both always agree on what needs attention.
export function buildAttention({ summary, journey, labReminders = [] }) {
  // One row per thing the patient has to do, each linking to where it is
  // cleared. The headline count is the sum of the rows — every open care plan
  // item counts individually, plus one for an unbooked follow-up and one for
  // results nobody has reviewed — so "3 items need attention" always matches
  // the three rows underneath it.
  //
  // summary.openActionItems already excludes [TEST] debris and blank doctor
  // notes the same way PatientCarePlanPage.jsx does (see
  // utils/carePlanItems.js), so these counts agree with that page's "Open".
  const openActionItems = summary.openActionItems ?? 0;
  const followUpBooked = !!journey.nextMilestone && !journey.nextMilestone.tentative;

  const attentionRows = [
    // Lab-day reminders first: a collection today or tomorrow is the most
    // time-sensitive thing on the list (the WhatsApp reminders go out at the
    // same moments — see utils/labTestReminders.js).
    ...labReminders.map(r => ({
      key: `lab-${r.id}`,
      icon: 'vial',
      title: r.title,
      meta: r.meta,
      to: r.to,
    })),
    ...(summary.openItems || []).map(item => ({
      key: `care-${item.id}`,
      icon: 'clipboard',
      title: isLabTestSuggestion(item.description) ? 'Lab tests to order' : shortItemTitle(item.description),
      meta: isLabTestSuggestion(item.description)
        ? (item.dueDate ? `Suggested by your doctor · due ${formatShortDate(item.dueDate)}` : 'Suggested by your doctor')
        : (item.dueDate ? `Care plan · due ${formatShortDate(item.dueDate)}` : 'Care plan'),
      // A doctor's lab-test suggestion goes straight to the order flow.
      to: isLabTestSuggestion(item.description) ? LAB_TEST_ORDER_PATH : '/patient/care-plan',
    })),
    ...(summary.lastResult ? [] : [{
      key: 'results',
      icon: 'document',
      title: 'Review your results',
      meta: 'Upload a report to get started',
      to: '/patient/records',
    }]),
    ...(followUpBooked ? [] : [{
      key: 'follow-up',
      icon: 'calendar',
      // A follow-up is a check-in with the patient's own Navigator, not a
      // doctor visit — send them to the Navigator booking card, not the
      // general doctor-booking flow.
      title: 'Book an appointment with your Navigator',
      meta: journey.nextMilestone
        ? `Tentative · ${formatShortDate(journey.nextMilestone.date)}`
        : 'Nothing scheduled yet',
      cta: 'Book',
      to: NAVIGATOR_BOOKING_PATH,
    }]),
  ];
  // Care-plan items reach us only as a count when no list came back; keep the
  // headline honest by padding with a single summary row in that case.
  if (openActionItems > 0 && !(summary.openItems || []).length) {
    attentionRows.unshift({
      key: 'care-count',
      icon: 'clipboard',
      title: `${openActionItems} care plan item${openActionItems === 1 ? '' : 's'}`,
      meta: 'Care plan',
      to: '/patient/care-plan',
    });
  }
  const pendingCount = (summary.openItems || []).length
    ? attentionRows.length
    : openActionItems + attentionRows.filter(r => r.key !== 'care-count').length;
  return { attentionRows, pendingCount, followUpBooked };
}

// Latest health score (0-100, higher is better) and its change since the
// baseline report — null score when no scored report exists yet.
export function summarizeHealthScore(journey, metric = 'health') {
  const past = asMetric(buildScoreNodes(journey.milestones, null), metric);
  return {
    past,
    score: past.length ? past[past.length - 1].healthScore : null,
    trendPct: computeTrendPct(past),
  };
}


// The 90-day outlook under the Health Score graph: where the score is predicted
// to be with the care plan's interventions and without them, and the change from
// today's score. The numbers come from the backend (journey.projection =
// { horizonDays?, withInterventions, withoutInterventions }, health-score scale,
// 0-100, higher is better); nothing is estimated here, so with no projection (or
// no current score) the section is simply absent. `isRisk` flips both onto the
// risk scale like the rest of the card, where a rise is the bad direction.
export function ProjectionOutlook({ projection, score, isRisk = false }) {
  if (!projection || score == null) return null;
  const horizon = Number(projection.horizonDays) || 90;
  const flip = v => (isRisk ? 100 - v : v);
  const rows = [
    { key: 'with', label: 'With interventions', value: projection.withInterventions },
    { key: 'without', label: 'Without interventions', value: projection.withoutInterventions },
  ].map(r => {
    const v = Number(r.value);
    if (r.value == null || !Number.isFinite(v)) return null;
    const target = Math.round(flip(v));
    const delta = target - Math.round(score);
    // Good = the score moved the way the metric wants (up for health, down for risk).
    const good = isRisk ? delta <= 0 : delta >= 0;
    return { ...r, target, delta, good };
  }).filter(Boolean);
  if (rows.length === 0) return null;

  return (
    <div className="today-outlook" role="group" aria-label={`Predicted ${isRisk ? 'risk' : 'health'} score in ${horizon} days`}>
      <div className="today-outlook__title">Predicted in {horizon} days</div>
      <div className="today-outlook__rows">
        {rows.map(r => (
          <div key={r.key} className={`today-outlook__tile today-outlook__tile--${r.key}`}>
            <div className="today-outlook__label">{r.label}</div>
            <div className="today-outlook__value">{r.target}</div>
            <div className={`today-outlook__delta${r.good ? ' is-good' : ' is-bad'}`}>
              {r.delta === 0 ? 'No change' : `${r.delta > 0 ? '+' : '−'}${Math.abs(r.delta)} from today`}
            </div>
          </div>
        ))}
      </div>
      <div className="today-outlook__note">A prediction, not a guarantee.</div>
    </div>
  );
}

// The Health Score card: the score-over-time graph plus a footer with the
// change since baseline. Shared by the Metric-oriented Today screen (footer
// greets the patient) and the Health Story page (showGreeting={false}: the
// footer names the score instead), so both always draw the same graph.
// allowMetricToggle (staff screens only) adds a Health score / Risk score switch, Health score first; patients only ever see the health score.
export function HealthScoreHero({ patient, journey, showGreeting = true, allowMetricToggle = false }) {
  const [metric, setMetric] = useState('health');
  const isRisk = allowMetricToggle && metric === 'risk';
  const metricName = isRisk ? 'Risk score' : 'Health score';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const { past, score, trendPct } = summarizeHealthScore(journey, isRisk ? 'risk' : 'health');
  // For the risk score a rise is the bad direction.
  const trendGood = trendPct != null && (isRisk ? trendPct <= 0 : trendPct >= 0);

  return (
    <div className="card today-hero-card" style={{ margin: 0, position: 'relative' }}>
      {allowMetricToggle && (
        <div role="group" aria-label="Score shown" style={{ position: 'absolute', top: 12, right: 14, zIndex: 2, display: 'inline-flex', gap: 4 }}>
          {[['health', 'Health score'], ['risk', 'Risk score']].map(([key, label]) => (
            <button key={key} type="button" aria-pressed={metric === key}
              className={`btn btn-sm ${metric === key ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setMetric(key)}>{label}</button>
          ))}
        </div>
      )}
      {/* Capped and centered so the line chart stays a readable "sparkline"
         density instead of stretching paper-thin across a wide desktop
         hero card — the surrounding score/greeting/trend text is free to
         use the card's full width either way. */}
      <div className="today-chart-wrap" style={allowMetricToggle ? { paddingTop: 30 } : undefined}>
        <HealthScoreChart journey={journey} metric={isRisk ? 'risk' : 'health'} />
      </div>

      <div className="today-hero-footer">
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
            {showGreeting ? greeting : metricName}
          </div>
          <div className="today-greeting-name" style={{ fontFamily: 'var(--font-display)', fontStyle: showGreeting ? 'italic' : 'normal', fontSize: 26, fontWeight: 700, color: 'var(--text-primary)' }}>
            {showGreeting ? `${patient?.firstName || 'there'}.` : (score != null ? Math.round(score) : '—')}
          </div>
        </div>
        {trendPct != null && (
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {/* The chart above only plots the most recent MAX_CHART_POINTS
                  reports, not full history — "Since" has to name that same
                  baseline (past[0]'s own date), not registration date
                  (journey.startedAt), or this line and the chart's own
                  leftmost label would disagree. */}
              Since {formatShortDate(past[0].date)}
            </div>
            <div className="today-trend" style={{
              fontSize: 13, fontWeight: 700, color: trendGood ? 'var(--teal)' : 'var(--amber-text)',
            }}>
              <span aria-hidden="true" style={{
                width: 6, height: 6, borderRadius: '50%',
                background: trendGood ? 'var(--teal)' : 'var(--amber)',
              }} />
              {isRisk
                ? `${trendPct > 0 ? '+' : ''}${trendPct}% since baseline`
                : (trendPct >= 0 ? `+${trendPct}% improved` : `${trendPct}% since baseline`)}
            </div>
          </div>
        )}
      </div>
      <ProjectionOutlook projection={journey?.projection} score={score} isRisk={isRisk} />
    </div>
  );
}

export default function PatientTodayView({ patient, summary, journey, labReminders = [] }) {
  const navigate = useNavigate();
  const [attentionOpen, setAttentionOpen] = useState(false);

  const { attentionRows, pendingCount } = buildAttention({ summary, journey, labReminders });
  const MAX_ATTENTION_ROWS = 3;
  const visibleRows = attentionRows.slice(0, MAX_ATTENTION_ROWS);
  const hiddenCount = pendingCount - visibleRows.length;

  return (
    <div className="page-shell page-shell--wide today-page" style={{ display: 'flex', flexDirection: 'column' }}>
      <VisitReadyCard />
      <HealthScoreHero patient={patient} journey={journey} />

      {/* ── Attention card ── */}
      {/* A single headline number backed by one tappable row per item, so the
          patient sees what the count is made of and can clear each one in a
          tap, instead of a number plus a cluster of identical pills. */}
      <section
        className={`today-attention ${pendingCount > 0 ? '' : 'today-attention--clear'}`}
        aria-label="Items needing your attention"
      >
        {pendingCount > 0 ? (
          <button
            type="button"
            className="today-attention-head today-attention-toggle"
            aria-expanded={attentionOpen}
            aria-controls="today-attention-list"
            onClick={() => setAttentionOpen(o => !o)}
          >
            <span className="today-attention-count" aria-hidden="true">{pendingCount}</span>
            <span className="today-attention-heading">
              {`${pendingCount} item${pendingCount === 1 ? '' : 's'} need${pendingCount === 1 ? 's' : ''} attention`}
            </span>
            <span className={`today-attention-chevron ${attentionOpen ? 'is-open' : ''}`} aria-hidden="true">
              <Icon name="chevronRight" size={16} />
            </span>
          </button>
        ) : (
          <div className="today-attention-head">
            <span className="today-attention-count" aria-hidden="true"><Icon name="check" size={14} /></span>
            <div className="today-attention-heading">You&rsquo;re all caught up</div>
          </div>
        )}
        {pendingCount > 0 && attentionOpen && (
          <ul className="today-attention-list" id="today-attention-list">
            {visibleRows.map(row => (
              <li key={row.key}>
                <button type="button" className="today-attention-row" onClick={() => navigate(row.to)}>
                  <span className="today-attention-icon" aria-hidden="true">
                    <Icon name={row.icon} size={16} />
                  </span>
                  <span className="today-attention-text">
                    <span className="today-attention-title">{row.title}</span>
                    <span className="today-attention-meta">{row.meta}</span>
                  </span>
                  {row.cta
                    ? <span className="today-attention-cta">{row.cta}</span>
                    : <Icon name="chevronRight" size={14} />}
                </button>
              </li>
            ))}
          </ul>
        )}
        {attentionOpen && hiddenCount > 0 && (
          <button type="button" className="today-attention-more" onClick={() => navigate('/patient/care-plan')}>
            +{hiddenCount} more in your care plan →
          </button>
        )}
      </section>

      {/* ── Your Chapters ── */}
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
            Your Chapters
          </div>
          <button type="button" onClick={() => navigate('/patient/timeline')} style={{
            border: 'none', background: 'none', color: 'var(--teal)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)',
          }}>
            View all →
          </button>
        </div>
        <div className="today-chapters-row">
          {journey.latestMilestone && (
            <ChapterCard
              accent="var(--red)"
              accentBg="var(--red-bg)"
              accentText="var(--red)"
              eyebrow="Latest milestone"
              date={journey.latestMilestone.date}
              title={formatMarkerText(journey.latestMilestone.label)}
              note={formatMarkerText(journey.latestMilestone.detail)}
              onClick={journey.latestMilestone.reportId
                ? () => navigate(`/patient/results/${journey.latestMilestone.reportId}`)
                : undefined}
            />
          )}
          {journey.nextMilestone && (
            <ChapterCard
              accent="var(--amber)"
              accentBg="var(--amber-bg)"
              accentText="var(--amber-text)"
              eyebrow="Upcoming milestone"
              date={journey.nextMilestone.date}
              title={formatMarkerText(journey.nextMilestone.label)}
              note={journey.nextMilestone.tentative ? 'Tentative — book to confirm' : formatMarkerText(journey.nextMilestone.detail)}
              noteAction={journey.nextMilestone.tentative ? 'Book' : null}
              onClick={() => navigate(journey.nextMilestone.tentative ? NAVIGATOR_BOOKING_PATH : '/patient/appointments')}
            />
          )}
        </div>
      </div>

      {/* ── Quick actions ── */}
      <div style={{ display: 'flex', gap: 12 }}>
        <QuickActionButton icon="importInto" label="Upload" sublabel="Add report" onClick={() => navigate('/patient/upload')} />
        <BookQuickAction navigate={navigate} />
      </div>

      <AskPreviuBar />
    </div>
  );
}
